// ============================================================================
// NPSDVehicleConnections.tsx - Connections management component for Vehicle
// Converted from Vue 2 Vue-A2021-NPSDVehicleConnections.ts
//
// Handles Vehicle-to-Person, Vehicle-to-Business, and Vehicle-to-Client
// connections.
// Uses shared NPSDConnectionPanel component for each panel.
// ============================================================================
import { useState, useCallback, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import { NPSDConnectionPanel } from '@/components/npsd/NPSDConnectionPanel';

import { App } from '@/services/app-compat';

interface DeleteConfirmState {
  open: boolean;
  type: string;
  item: any;
  name: string;
}

interface NPSDVehicleConnectionsProps {
  people?: any[];
  businesses?: any[];
  clients?: any[];
  isReadOnly?: boolean;
  vehicleId?: number | string | null;
  onAddConnection?: (type: string) => void;
  onViewConnection?: (type: string, item: any) => void;
  onRemoveConnection?: (type: string, id: any) => void;
}

export function NPSDVehicleConnections({
  people = [],
  businesses = [],
  clients = [],
  isReadOnly = false,
  vehicleId = null,
  onAddConnection,
  onViewConnection,
  onRemoveConnection,
}: NPSDVehicleConnectionsProps) {
  const { theme } = useTheme();

  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
    open: false,
    type: '',
    item: null,
    name: '',
  });

  // Computed: isEditMode is the inverse of isReadOnly
  const isEditMode = useMemo(() => !isReadOnly, [isReadOnly]);

  // Add connection - callback to parent
  const handleAddConnection = useCallback(
    (connectionType: string) => {
      if (isReadOnly) {
        App?.showPopupNotification('Edit mode required.', 'warning');
        return;
      }
      onAddConnection?.(connectionType);
    },
    [isReadOnly, onAddConnection]
  );

  // View item - callback to parent
  const handleViewItem = useCallback(
    (connectionType: string, item: any) => {
      onViewConnection?.(connectionType, item);
    },
    [onViewConnection]
  );

  // Remove connection - open confirmation
  const handleRemoveConnection = useCallback(
    (connectionType: string, item: any) => {
      if (isReadOnly) {
        App?.showPopupNotification('Edit mode required.', 'warning');
        return;
      }

      let name = '';
      switch (connectionType) {
        case 'person':
          name = `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'this person';
          break;
        case 'business':
          name = item.businessName || 'this business';
          break;
        case 'client':
          name = item.name || 'this client';
          break;
      }

      setDeleteConfirm({
        open: true,
        type: connectionType,
        item: item,
        name: name,
      });
    },
    [isReadOnly]
  );

  // Confirm delete
  const doDelete = useCallback(() => {
    const { type, item } = deleteConfirm;
    if (!item) {
      setDeleteConfirm({ open: false, type: '', item: null, name: '' });
      return;
    }

    onRemoveConnection?.(type, item.id);
    setDeleteConfirm({ open: false, type: '', item: null, name: '' });
  }, [deleteConfirm, onRemoveConnection]);

  // Cancel delete
  const cancelDelete = useCallback(() => {
    setDeleteConfirm({ open: false, type: '', item: null, name: '' });
  }, []);

  return (
    <div className={`npsd-vehicle-connections ${theme}`}>
      {/* Person Connections */}
      <NPSDConnectionPanel
        connections={people}
        connectionType="person"
        title="Person Connections"
        isEditMode={isEditMode}
        onAddConnection={handleAddConnection}
        onViewItem={handleViewItem}
        onRemoveConnection={handleRemoveConnection}
      />

      {/* Business Connections */}
      <NPSDConnectionPanel
        connections={businesses}
        connectionType="business"
        title="Business Connections"
        isEditMode={isEditMode}
        onAddConnection={handleAddConnection}
        onViewItem={handleViewItem}
        onRemoveConnection={handleRemoveConnection}
      />

      {/* Client Connections */}
      <NPSDConnectionPanel
        connections={clients}
        connectionType="client"
        title="Client Connections"
        isEditMode={isEditMode}
        onAddConnection={handleAddConnection}
        onViewItem={handleViewItem}
        onRemoveConnection={handleRemoveConnection}
      />

      {/* Confirm Delete Modal */}
      <ConfirmModal
        open={deleteConfirm.open}
        title={`Remove ${deleteConfirm.name}?`}
        messageHtml="Are you sure you want to remove this connection?"
        confirmText="Remove"
        cancelText="Cancel"
        confirmClass="danger"
        theme={theme}
        onConfirm={doDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}

(window as any).NPSDVehicleConnectionsReact = NPSDVehicleConnections;
